import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { isMobile } from 'react-device-detect';

import { useTranslation, Trans } from 'react-i18next';

import { useTheme, CircularProgress, IconButton, Menu, MenuItem } from '@mui/material';
import { Rectangle, Dashboard, Article, MoreVert } from '@mui/icons-material';

import { Canvas, useThree } from '@react-three/fiber';
import { Stats } from '@react-three/drei';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

import Background from '../../../partials/project/view/Background';
import Lights from '../partials/Lights';
import Cameras from '../../../partials/project/view/Cameras';
import Controls from '../../../partials/project/view/Controls';
import Floor from '../../../partials/project/view/Floor';
import { LoadingScreen } from '../../../partials/LoadingScreen';
import StyledIconButton from '../../../partials/easyFloor/StyledIconButton';
import CubeIcon from '../../../icons/CubeIcon';
import CubesIcon from '../../../icons/CubesIcon';
import { CameraAlt } from '@mui/icons-material';

import { calcCameraDistance } from '../../../../utils/Utils';
import apiClient from '../../../../services/api';
import store from '../../../../store';
import { setElements, setMesh, setMeshId, setOptimizedView, setProjectElementId } from '../../../../store/shortcuts/Project';
import { setSideBarIsOpened } from '../../../../store/actions/Store';
import { toggleMessageOpen } from '../../../../store/actions/Message';
import { setSelectedMeshId } from '../../../../store/actions/Project';

import * as THREE from 'three';

const SHADOWS = false;
const CAMERA_DISTANCE = 2500;

const Model = React.memo((props) => {
    const { scene, groupProject, setGroupProject, isOptimizedView } = props;
    const copiedScene = React.useMemo(() => scene.clone(), [scene]);
    const group = useRef();
    const gl = useThree();

    const selectedMeshID = useSelector((state) => state.project.selectedMeshId);

    const [originalColors, setOriginalColors] = useState([]);

    useEffect(() => {
        if (!scene) {
            return;
        }

        if (!isOptimizedView) {
            let colors = [];

            scene.children.forEach((children) => {
                children.traverse((object) => {
                    if (object.isMesh) {
                        const color = new THREE.Color(
                            object.material.color.r,
                            object.material.color.g,
                            object.material.color.b,
                        );

                        colors[object.uuid] = color;
                    }
                });
            });

            setOriginalColors(colors);
        }

        scene.traverse((object) => {
            if (object.isMesh === true && object.material.map !== null) {
                object.material.map.anisotropy = gl?.gl.capabilities.getMaxAnisotropy();
            }
        });

        groupProject.add(scene);

        setGroupProject(groupProject);
    }, [scene]);

    useEffect(() => {
        if (isOptimizedView) {
            return;
        }

        scene.children.forEach((children) => {
            children.traverse((object) => {
                if (object.isMesh === true) {

                    object.material.color.set(originalColors[object.uuid]);

                    const { userData } = object;

                    if (userData?.EBMeshId == selectedMeshID) {
                        object.material.color.set('#00FF00');
                    }
                }
            });
        })
    }, [selectedMeshID]);

    return (
        <group ref={group} dispose={null}>
            <primitive
                object={copiedScene}
                onClick={(event) => {
                    event.stopPropagation();

                    if (isOptimizedView) {
                        return;
                    }

                    const { userData, name } = event.intersections[0].object;
                    const { EBMeshId, EBProjectElementId } = userData;
                    let parentEBProjectElementId = null;

                    if (!EBProjectElementId) {
                        const parentUserData = event.intersections[0].object?.parent?.userData;

                        parentEBProjectElementId = parentUserData?.EBProjectElementId;
                    }

                    if (selectedMeshID && selectedMeshID == EBMeshId) {
                        setSelectedMeshId(null);

                        setMeshId(null);
                        setProjectElementId(null);
                    } else {
                        setSelectedMeshId(EBMeshId);

                        setMeshId(EBMeshId);
                        setProjectElementId(EBProjectElementId ? EBProjectElementId : parentEBProjectElementId);
                    }

                    setMesh(event.intersections[0].object);
                }}
            />
        </group>
    )
});

const ThemedBackground = () => {
    const theme = useTheme();
    return <Background color={theme.palette.background.default} />;
};

const ThemedFloor = () => {
    const theme = useTheme();
    return <Floor color={theme.palette.border.grid} />;
};

function getWindowDimensions() {
    const { innerWidth: width, innerHeight: height } = window;

    return { width, height };
}

const FloatingButtons = ({
    viewType,
    setViewType,
    id,
    isOptimizedView,
    handleOptimizedViewChange
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const sideBarIsOpened = useSelector((state) => state.project.sideBarIsOpened);
    const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

    useEffect(() => {
        function handleResize() {
            setWindowDimensions(getWindowDimensions());
        }

        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const style = { position: 'absolute', top: '50%', right: '20px', zIndex: 10, transform: 'translateY(-50%)' }

    return (
        <div style={style}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <StyledIconButton
                    color="primary"
                    title={t('Change View')}
                    isActive={false}
                    onClick={() => {
                        if (viewType == '3D') {
                            setViewType('2D');
                        } else {
                            setViewType('3D');
                        }
                    }}
                >
                    {viewType != '3D' ? <CubeIcon /> : <CubesIcon />}
                </StyledIconButton>
                <StyledIconButton
                    color="primary"
                    isActive={false}
                    onClick={handleOptimizedViewChange}
                >
                    {isOptimizedView ? <Rectangle /> : <Dashboard />}
                </StyledIconButton>

                <StyledIconButton
                    color="primary"
                    title={t('Show report')}
                    isActive={sideBarIsOpened}
                    onClick={() => { store.dispatch(setSideBarIsOpened(sideBarIsOpened ? false : true)); }}
                >
                    <Article />
                </StyledIconButton>
            </div>
        </div>
    );
};

function ProjectEditorV2({abort}) {
    const { t } = useTranslation();
    const ref = useRef();
    const componentMounted = useRef(true);

    const projectData = useSelector((state) => state.project.selectedProject);
    const projectIsLoading = useSelector((state) => state.project.projectIsLoading);
    const usdzUploadProgress = useSelector((state) => state.project.usdzUploadProgress);
    const isOptimizedView = useSelector((state) => state.project.isOptimizedView);

    const [viewType, setViewType] = useState('3D');
    const [center, setCenter] = useState({ x: 0, y: 0, z: 0 });
    const [distance, setDistance] = useState(CAMERA_DISTANCE);
    const [panZoomEnabled, setPanZoomEnabled] = useState(true);
    const [stats, setStats] = useState(1);
    const [loadedModels, setLoadedModels] = useState([]);

    //const [selectedMeshID, setSelectedMeshID] = useState(null);
    const [modelsLoaded, setModelsLoaded] = useState(false);
    const [scene, setScene] = useState(null);
    const [groupProject, setGroupProject] = useState(new THREE.Group);
    const [loadingProgress, setLoadingProgress] = useState(null);

    const is3D = viewType === '3D';
    const id = projectData?.project?.id;
    const projectIsLoaded = !!projectData;

    useEffect(() => {
        if (!projectData) {
            return;
        }

        setLoadedModels([]);

        loadModelsChunked(projectData);
    }, [isOptimizedView]);

    useEffect(() => {
        componentMounted.current = true;
        updateAsync();
        return () => { componentMounted.current = false };
    }, [projectData]); // eslint-disable-line react-hooks/exhaustive-deps

    const updateAsync = async () => {
        if (componentMounted.current) {
            if (projectData !== null) {
                /*
                if (!getModel()) {
                    setElements(projectData.project_elements);
                } else {
                */
                loadAndSetProjectElements();
                /*}*/

                const center = calcProjectElementsCenter(projectData);
                setCenter(center);

                // const distance = calcCameraDistance(size);
                const distance = calcCameraDistance(center, true, 25);
                setDistance(distance);
            } else {
                setCenter(calcProjectCenter({}));
                setDistance(CAMERA_DISTANCE);
            }
        }
    };

    const calcProjectCenter = ({ w = 100, h = 100, d = 100 }) => {
        const x = w / 2;
        const y = h / 2;
        const z = d / 2;

        return { x, y, z };
    };

    const calcProjectElementsCenter = (data, model) => {
        return {
            x: Number(data.project.width) / 2,
            y: Number(data.project.height) / 2,
            z: Number(data.project.depth) / 2,
            w: Number(data.project.width),
            h: Number(data.project.height),
            d: Number(data.project.depth),
        };
    };

    const loadAndSetProjectElements = () => {
        apiClient.post('/api/projects/load-project-elements', { id: Number(id) }).then((response) => {
            if (!response.data.success) return;
            setElements(response.data.project_elements);

            console.log(response);
        }).catch(error => {
            store.dispatch(toggleMessageOpen(t('An error occurred while loading project elements'), 'error'));
        });
    };

    async function loadModelsChunked(projectData) {
        let counter = 0;
        const data = !isOptimizedView ? projectData.models : projectData.models_optimized;

        while (counter < data?.length) {
            const result = await loadChunk(counter, counter + 50);

            counter += 50;

            setLoadingProgress({ loaded: counter, total: projectData?.models?.length });
        }

        setModelsLoaded(true);
    }

    function loadChunk(start, end) {
        const loader = new GLTFLoader();
        const data = !isOptimizedView ? projectData.models : projectData.models_optimized;

        return new Promise((resolve, reject) => {
            data.slice(start, end).forEach((url, index) => {
                loader.load(
                    url,
                    (gltf) => {
                        setLoadedModels((prevModels) => [
                            ...prevModels,
                            { url, scene: gltf.scene, gltf: gltf },
                        ]);

                        resolve(gltf);
                    },
                    undefined,
                    (error) => {
                        console.error(`Error loading model at ${url}:`, error);
                    }
                );
            });
        });
    }

    function saveScreenshot() {
        const data = ref.current.toDataURL('image/png');

        apiClient.post('/api/projects/save-screenshot', { id, data })
            .then(response => {
                if (response.data.success) {
                    store.dispatch(toggleMessageOpen(t('Screenshot saved successfully!'), 'success'));
                } else {
                    store.dispatch(toggleMessageOpen(t('An error occurred while saving screenshot!'), 'error'));
                }
            })
            .catch(error => {
                store.dispatch(toggleMessageOpen(t('An error occurred while saving screenshot!'), 'error'))
            });
    }

    const Helper = ({ setScene, groupProject }) => {
        useEffect(() => {
            if (modelsLoaded) {
                setScene(groupProject);
            }
        }, [groupProject]);
    }

    /**
     * Change optimized view type listener
     */
    const handleOptimizedViewChange = () => {
        setOptimizedView(!isOptimizedView);
    };

    return (
        <div style={{
            position: 'relative',
            overflow: 'hidden',
            height: '100%',
            flexGrow: 1,
            width: '100'
        }}
        >
            <Canvas
                shadows={SHADOWS}
                gl={{ preserveDrawingBuffer: true, precision: 'lowp' }}
                style={{ cursor: 'pointer' }}
                ref={ref}
            >
                <ThemedBackground />
                <Lights shadows={SHADOWS} />
                <Cameras perspective={is3D} position={{ ...center, z: distance }} />
                <Controls rotate={is3D} target={{ ...center, z: 0 }} enabled={panZoomEnabled} />
                {is3D && <ThemedFloor />}
                {!abort && modelsLoaded && loadedModels.map((model, index) => (
                    <Model
                        key={index}
                        scene={model.scene}
                        groupProject={groupProject}
                        setGroupProject={setGroupProject}
                        isOptimizedView={isOptimizedView}
                    />
                ))}
                {modelsLoaded && <Helper setScene={setScene} groupProject={groupProject} />}
                {/*<Stats className="three-stats" showPanel={stats} onClick={() => setStats(v => v < 2 ? v + 1 : 0)} />*/}
            </Canvas>
            {projectIsLoading && <LoadingScreen text="Downloading Project" />}

            {!modelsLoaded && (
                <LoadingScreen
                    text={`Downloading Project`}
                />
            )}
            {
                projectIsLoaded &&
                <IconButton
                    onClick={() => saveScreenshot(id, ref)}
                    style={{
                        position: 'absolute',
                        bottom: '5px',
                        right: '5px'
                    }}
                    color='primary'
                >
                    <CameraAlt />
                </IconButton>
            }
            {projectIsLoaded &&
                <FloatingButtons
                    viewType={viewType}
                    setViewType={setViewType}
                    id={id}
                    isOptimizedView={isOptimizedView}
                    handleOptimizedViewChange={handleOptimizedViewChange}
                />
            }
        </div>
    );
}

export default ProjectEditorV2;
